import {GameStateEnums} from '../../../../shared/GameEnums.ts'
import {type UIGameMechanic} from '../../../shared/scripts/ui_game/UIGameEventController.ts'
import {type UIGameManager} from '../../../shared/scripts/ui_game/UIGameManager.ts'
import {setElementInnerText} from '../../../shared/scripts/ui_utils/DomUtils.ts'
import {type TranslationObject} from '@colonist/i18n'
import {UIUtilsEjsVariables} from '../../../shared/scripts/UIUtilsEjsVariables.ts'
import {TurnState} from '@colonist/game-states'
import FaviconNotification from '@static/images/favicon_notification.png'

export class UIWGameTabVisibilityController implements UIGameMechanic {
    private readonly uiGameManager: UIGameManager
    private readonly pageTitle: HTMLTitleElement = document.getElementById('browser_tab') as HTMLTitleElement
    private readonly favicon: HTMLLinkElement = document.getElementById('favicon') as HTMLLinkElement
    private readonly NOTIFICATION_BLINK_INTERVAL = 2000
    private readonly visibilityChangeListener: () => void

    private readonly defaultTitle: string
    private readonly defaultFavIcon: string

    private actionNeededBlinkInterval: NodeJS.Timeout | undefined
    private isColonistTabHidden: boolean = false
    private shouldDisplaySecondText: boolean = true
    private isGameFinished: boolean = false

    constructor(uiGameManager: UIGameManager) {
        if(UIUtilsEjsVariables.isUserOnDiscord) return
        if(uiGameManager.gameController.isReplay) return
        if(uiGameManager.gameController.isBotGame()) return

        this.uiGameManager = uiGameManager
        this.defaultTitle = this.pageTitle.innerHTML
        this.defaultFavIcon = this.favicon.href

        uiGameManager.eventController.registerUIGameMechanic(this)
        this.visibilityChangeListener = () => this.onVisibilityChange()
        document.addEventListener('visibilitychange', this.visibilityChangeListener)
    }

    onEvent(data: any, eventType: GameStateEnums.UIGameEventEnums) {
        switch(eventType) {
            case GameStateEnums.UIGameEventEnums.PlayerControllerStateUpdated:
            case GameStateEnums.UIGameEventEnums.TradeStateUpdated:
            case GameStateEnums.UIGameEventEnums.GameStateUpdated:
            case GameStateEnums.UIGameEventEnums.GameEnd:
                this.onStateUpdates(); break
            case GameStateEnums.UIGameEventEnums.ClientReconnectionTriggered:
                this.destroy(); break
            default: break
        }
    }

    private resetTabTitleAndFavIcon() {
        setElementInnerText(this.pageTitle, {key: 'strings:colonistCommon.utils.#buffer', options: {value: this.defaultTitle}})
        this.favicon.href = this.defaultFavIcon
    }

    private triggerActionNeededBlinking() {
        clearInterval(this.actionNeededBlinkInterval)
        this.isColonistTabHidden ? this.updateTabTitleAndFavIcon() : this.resetTabTitleAndFavIcon()
        this.actionNeededBlinkInterval = setInterval(() => this.isColonistTabHidden ? this.updateTabTitleAndFavIcon() : this.resetTabTitleAndFavIcon(), this.NOTIFICATION_BLINK_INTERVAL)
    }

    private updateTabTitleAndFavIcon() {
        const actionNeededText: TranslationObject = {key: 'strings:game.gameState.browserBlinkings.actionNeeded'}
        const secondText: TranslationObject = this.isGameFinished ? {key: 'strings:game.gameState.turn.gameEndSelf'} : {key: 'strings:game.gameState.browserBlinkings.playersWaiting'}

        if(this.shouldDisplaySecondText) setElementInnerText(this.pageTitle, secondText)
        else setElementInnerText(this.pageTitle, actionNeededText)

        this.favicon.href = FaviconNotification
        this.shouldDisplaySecondText = !this.shouldDisplaySecondText
    }

    private stopActionNeededBlinking() {
        if(this.actionNeededBlinkInterval == undefined) return
        clearInterval(this.actionNeededBlinkInterval)
        this.resetTabTitleAndFavIcon()
    }

    private onStateUpdates() {
        if(this.uiGameManager.gameController == undefined) return

        const amITakingAction = this.uiGameManager.gameController.amITakingAction()
        this.isGameFinished = this.uiGameManager.gameController.currentState.turnState == TurnState.GameEnd
        if(this.isGameFinished) this.uiGameManager.eventController.unregisterUIGameMechanic(this)

        const shouldStateUpdateTriggerBlinking = amITakingAction || this.isGameFinished
        shouldStateUpdateTriggerBlinking ? this.triggerActionNeededBlinking() : this.stopActionNeededBlinking()
    }

    private onVisibilityChange() {
        if(this.uiGameManager.gameController == undefined) return

        this.isColonistTabHidden = document.hidden == true
        this.uiGameManager.eventController.sendEvent(document.hidden == false, GameStateEnums.UIGameEventEnums.GameVisibilityUpdated)
        if(this.isGameFinished) return this.stopActionNeededBlinking()

        const shouldVisibilityChangeTriggerBlinking = this.uiGameManager.gameController.amITakingAction() && !this.isGameFinished
        shouldVisibilityChangeTriggerBlinking ? this.triggerActionNeededBlinking() : this.stopActionNeededBlinking()
    }

    private destroy() {
        document.removeEventListener('visibilitychange', this.visibilityChangeListener)
    }
}
